'use client'

import type { ElementType } from 'react'

type CassieSettings = {
  environment: string
  languageCode: string
  licenseKey: string
  region: string
  widgetProfileId: number
}

type CassieWidgetLoaderInstance = {
  Widget?: {
    hideModal: () => void
    widgetTemplate?: { hasConsent?: boolean }
  }
}

type CassieWindow = Window & {
  CassieWidgetLoader?: CassieWidgetLoaderInstance
  CassieWidgetLoaderModule?: new (settings: CassieSettings) => CassieWidgetLoaderInstance
}

type Params = {
  /** Script component to render with, so this stays free of a direct next/script dependency. */
  as: ElementType
  /**
   * Origin of the Cassie CDN, including protocol and without a trailing slash.
   * prod: https://cscript-cdn-irl.cassiecloud.com
   * uat:  https://cscript-cdn-irl-uat.cassiecloud.com
   */
  cassieHost?: string
  environment: string
  licenseKey?: string
  locale: string
}

const LOADER_SCRIPT_ID = 'cassieLoaderId'
const ACTIVE_SLIDER_CLASS = 'cassie-toggle-switch--slider--active'

// Cassie renders its widget outside React, so its template is patched imperatively once it is ready.
const addStrictlyNecessaryToggleSwitch = (): void => {
  const header = document.getElementById('cassie_strictly_necessary')?.querySelector('.cassie-cookie-modal--group-head-container')
  if (!header) return

  const toggle = document.createElement('div')
  toggle.classList.add('cassie-toggle-switch', 'cassie-cookie-group--toggle-switch')
  toggle.setAttribute('role', 'switch')
  toggle.setAttribute('aria-checked', 'true')
  toggle.setAttribute('aria-disabled', 'true')

  const slider = document.createElement('span')
  slider.classList.add('cassie-toggle-switch--slider', 'cassie-cookie-group--toggle-switch--slider', ACTIVE_SLIDER_CLASS)
  toggle.appendChild(slider)

  header.insertBefore(toggle, header.children[1] ?? null)

  // "Accept all" clears every switch, so the strictly necessary one has to be re-asserted.
  document.getElementById('cassie_accept_all_toggle_switch')?.addEventListener('click', () => {
    slider.classList.add(ACTIVE_SLIDER_CLASS)
    toggle.setAttribute('aria-checked', 'true')
  })
}

const autoToggleBasedOnConsent = (loader: CassieWidgetLoaderInstance): void => {
  if (loader.Widget?.widgetTemplate?.hasConsent) return
  document.querySelectorAll('.cassie-child-cookie--toggle-switch--slider').forEach((slider) => slider.classList.add(ACTIVE_SLIDER_CLASS))
}

// The native app passes app_consent when it has already collected consent itself.
const hideModalWhenAppHandlesConsent = (loader: CassieWidgetLoaderInstance): void => {
  const appConsent = new URLSearchParams(window.location.search).get('app_consent')
  if (appConsent === 'true' || appConsent === 'false') loader.Widget?.hideModal()
}

const CookieScript = ({ as, cassieHost, environment, licenseKey, locale }: Params) => {
  const Script = as

  if (!cassieHost || !licenseKey) return null

  const initialiseWidget = (): void => {
    const cassieWindow = window as CassieWindow
    if (!cassieWindow.CassieWidgetLoaderModule || cassieWindow.CassieWidgetLoader) return

    const loader = new cassieWindow.CassieWidgetLoaderModule({
      environment: environment === 'prod' ? 'production' : 'uat',
      languageCode: locale,
      licenseKey,
      region: 'irl',
      widgetProfileId: 1,
    })
    cassieWindow.CassieWidgetLoader = loader

    document.addEventListener(
      'CassieTemplateInitialized',
      () => {
        addStrictlyNecessaryToggleSwitch()
        autoToggleBasedOnConsent(loader)
        hideModalWhenAppHandlesConsent(loader)
      },
      { once: true }
    )
  }

  return <Script id={LOADER_SCRIPT_ID} onLoad={initialiseWidget} src={`${cassieHost}/loader.js`} strategy="afterInteractive" />
}

export default CookieScript
